import PrimaryList from 'common/PrimaryList';
import { DocumentNode } from 'graphql';
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useMatch, useNavigate } from 'react-router-dom';
import FilterDrawer from 'styleguide/ModalDrawer';
import Filters from '../Filters';
import { Context } from '../Context';
import { useLDFlagValue } from 'common/useLDFlagValue';
import { HOME_HEALTH_V2_FLAG } from 'common/constants';

type Props = {
  card: React.ElementType;
  query: DocumentNode;
  title: string | undefined;
  baseUrl?: string;
  type: string;
  listType: string;
  paramId?: string;
  textCreate?: string;
  active?: string;
  dataList?: any;
  onSelect?: (careFacility: any) => void;
  payloadvariables?: any;
};

const Container: React.FC<Props> = ({
  card,
  query,
  title,
  baseUrl,
  type,
  listType,
  paramId,
  textCreate,
  active = 'true,false',
  onSelect,
  dataList,
  payloadvariables,
}) => {
  const isHomeHealthV2FlagEnabled = useLDFlagValue(
    HOME_HEALTH_V2_FLAG
  );

  const {
    homeHealthFilters,
    homeHealthDefaultFiltersApplied,
    dispatch,
  } = useContext(Context);
  const navigate = useNavigate();

  const isCreatePath = useMatch({
    path: `${baseUrl}/new`,
  });
  const [searchEncounterQuery, setSearchEncounterQuery] = useState<string>('');

  const defaultStatuses = useMemo(() => ['true', 'false'], []);

  const [openFilterDrawer, setOpenFilterDrawer] = useState(false);

  const [filterOptions, setFilterOptions] = useState<any>(homeHealthFilters || {});

  const handleSearch = useCallback((value: string) => {
    setSearchEncounterQuery(value);
  }, []);

  const handleCreate = useCallback(() => {
    const url = `${baseUrl}/new`;
    navigate(url);
  }, [navigate, baseUrl]);

  const handleSelection = useCallback(
    (selection: any) => {
      const url = `${baseUrl}/${selection?.id}`;
      if (paramId) {
        navigate(url, {
          state: { info: selection },
          replace: true,
        });
      } else {
        navigate(url, {
          state: { info: selection },
        });
      }
    },
    [navigate, paramId, baseUrl]
  );

  useEffect(() => {
    if (homeHealthDefaultFiltersApplied) {
      dispatch({ type: 'setHomeHealthFilters', value: filterOptions });
    } else {
      const defaultOptions = {
        ...filterOptions,
        active: defaultStatuses,
      };
      setFilterOptions(defaultOptions);
      dispatch({ type: 'setHomeHealthFilters', value: defaultOptions });
      dispatch({ type: 'setHomeHealthDefaultFiltersApplied', value: true });
    }
  }, [defaultStatuses, homeHealthDefaultFiltersApplied, dispatch]);
  
  const filterCount = useMemo(() => {
    let count = 0;
    if (filterOptions?.active?.length) count++;
    if (filterOptions?.states?.length) count++;
    return count;
  }, [filterOptions]);

  const filterVariables = useMemo(() => {
    return {
      active: filterOptions?.active?.join(','),
      state: filterOptions?.states?.join(','),
      tag: 'import-2025',
    }
  }, [filterOptions]);

  const dataListProps = useMemo(() => {
    const baseProps = {
      component: card,
      onCreate: handleCreate,
      onSelect: onSelect ? onSelect : handleSelection,
      onSearch: handleSearch,
      searchQuery: searchEncounterQuery,
      selectedId: paramId,
      textCreate: textCreate,
      canCreate: !isCreatePath,
    };
  
    const conditionalProps = (type === 'home-health' && isHomeHealthV2FlagEnabled) ? {
      onToggleTune: () => setOpenFilterDrawer(true),
      filterCount: filterCount,
    } : {};
  
    return dataList || {
      ...baseProps,
      ...conditionalProps,
    };
  }, [
    dataList,
    card,
    handleCreate,
    onSelect,
    handleSelection,
    handleSearch,
    searchEncounterQuery,
    paramId,
    textCreate,
    isCreatePath,
    filterCount
  ]);

  const variables = useMemo(
    () =>
      payloadvariables || {
        ...(searchEncounterQuery?.length && {
          term: searchEncounterQuery,
        }),
        first: 25,
        sort: '-_lastUpdated',
        type: type,
        active: active,
        ...(type === 'home-health' && isHomeHealthV2FlagEnabled && filterVariables),
      },
    [active, searchEncounterQuery, type, payloadvariables, filterVariables]
  );

  return (
    <>
      <FilterDrawer
        open={openFilterDrawer}
        onClose={() => setOpenFilterDrawer(false)}
        title="Filters"
      >
        <Filters
          values={filterOptions}
          defaultStatuses={defaultStatuses}
          defaultStates={[]}
          onApply={(values) => {
            setFilterOptions(values);
            setOpenFilterDrawer(false);
          }}
          onCancel={() => setOpenFilterDrawer(false)}
        />
      </FilterDrawer>

      <PrimaryList
        type={listType}
        query={query}
        variables={variables}
        dataListProps={dataListProps}
        title={title}
        fullRow
      />
    </>
  );
};
export default Container;
